import { useParams } from "react-router-dom";

import { TabSideNavigationItem } from "@shared";

import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import withAuthorization from "@/common/hocs/with-authorization/withAuthorization";
import { useRedirectWhenMissing } from "@/common/hooks/useRedirectWhenMissing";
import { DetailPageLayout } from "@/pages/system-settings/components/detail-page/DetailPageLayout";
import { useActiveDetailTab } from "@/pages/system-settings/components/detail-page/hooks/useActiveDetailTab";
import { formatFullName } from "@/pages/system-settings/tabs/counselors/CounselorList.utils";
import {
  CounselorByIdParams,
  SystemSettingsCounselorTab,
  SystemSettingsTab,
  systemSettingsPath,
} from "@/router/SystemSettingsRoutes";
import { useTranslation } from "@/translations";

import { useCounselor } from "./hooks/useCounselor";

function CounselorDetailInner() {
  const { t } = useTranslation();
  const { id } = useParams<CounselorByIdParams>();
  const { counselor, isLoading } = useCounselor();
  const activeTab = useActiveDetailTab(SystemSettingsTab.Counselors, SystemSettingsCounselorTab);

  const counselorsListPath = systemSettingsPath.byTab(SystemSettingsTab.Counselors);
  useRedirectWhenMissing(isLoading, counselor, counselorsListPath);

  if (!counselor || !id) {
    return null;
  }

  const navigationItems: TabSideNavigationItem[] = [
    {
      key: SystemSettingsCounselorTab.PersonalData,
      label: t("counselors.tabs.personalData"),
      to: systemSettingsPath.byCounselorTab({ id, tab: SystemSettingsCounselorTab.PersonalData }),
    },
    {
      key: SystemSettingsCounselorTab.WorkTime,
      label: t("counselors.tabs.workTime"),
      to: systemSettingsPath.byCounselorTab({ id, tab: SystemSettingsCounselorTab.WorkTime }),
    },
    {
      key: SystemSettingsCounselorTab.DutyPreferences,
      label: t("counselors.tabs.dutyPreferences"),
      to: systemSettingsPath.byCounselorTab({ id, tab: SystemSettingsCounselorTab.DutyPreferences }),
    },
  ];

  return (
    <DetailPageLayout
      title={formatFullName(counselor)}
      backTo={counselorsListPath}
      backAriaLabel={t("counselors.backAria")}
      navigationItems={navigationItems}
      activeTabKey={activeTab}
      context={counselor}
    />
  );
}

export const CounselorDetail = withAuthorization(CounselorDetailInner, UserPermission.Supervisor);
